/* eslint-disable no-debugger */

import React, { useState, Fragment, cloneElement, FC, useEffect } from 'react';
import {
  useDataProvider,
  List,
  TextField,
  GetListResult,
  useNotify,
  sanitizeListRestProps,
  TopToolbar,
  useListContext,
  FunctionField,
  Pagination,
  FunctionFieldProps,
  ListProps,
  PublicFieldProps,
  InjectedFieldProps,
} from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import Datagrid from 'components/Datagrid';
import {
  JobPendingResourceDto,
  ContractorDto,
  JobPendingResourceInterface,
  JobPhaseInterface,
  JobPhaseOwnableInterface,
  PhaseTypeEnum,
} from '@vatos-pas/common';
import Box from '@material-ui/core/Box';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import ConfirmDialog from 'components/ConfirmDialog';
import { RmMasterSheetFilter } from '../filters/rm-master-sheet-filters';
import { exporter } from 'providers/export';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { UIProvider } from 'providers/uiProvider';
import { CAN_ASSIGN_CONTRACTOR } from 'providers/permissions';
import InfoDialog from 'components/InfoDialog';
import { WithoutTimezoneDateField } from 'modules/master-sheet-v2/views';
import CustomExportButton from 'components/CustomExportButton';
import { CircularProgress } from '@material-ui/core';
import DialogTitle from '@material-ui/core/DialogTitle';
import { SupervisorField } from 'components/SupervisorListItems';
import { trackEvent } from 'utils/mixpanel';
import {
  useContractorCapacityStatus,
  usePreSelectedContractorCapacity,
  PageContractorCapacityContext,
} from 'hooks';
import type { PreSelectedRecord } from 'hooks';
import { ContractorCapacityLabel } from 'components/ContractorCapacityLabel';
import { phaseTypeToResourceType } from 'utils/phaseTypeToResourceType';
import { PHASE_CONTRACTOR_LIST_LIMIT } from 'utils/phaseUtils';
import { contractorFilterForPhase } from 'utils/contractorFilter';
import { useAssignContractor } from '../hooks/useAssignContractor';

const JobPhaseSprayReadyHourEnum = {
  8: '8am',
  9: '9am',
  10: '10am',
  11: '11am',
  12: '12pm',
  13: '1pm',
  14: '2pm',
  Now: 'Now',
  Done: 'Done',
};

const useStyles = makeStyles({
  button: {
    marginLeft: '10px',
  },
  content: {
    '@media(max-width: 1300px)': {
      overflow: 'auto',
    },
  },
  contentClosed: {
    '@media(max-width: 1300px)': {
      overflow: 'auto',
    },
  },
  root: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 260px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
  rootClosed: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 125px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
});

const Ready: FC<
  Omit<FunctionFieldProps<JobPendingResourceInterface>, 'render'>
> = props => (
  <FunctionField<JobPendingResourceInterface>
    {...props}
    render={record => (
      <label>
        {record?.jobPhaseReadyHour &&
          JobPhaseSprayReadyHourEnum[record.jobPhaseReadyHour]}
      </label>
    )}
  />
);

const lateVerify = (record: JobPendingResourceDto & any) => {
  let backgroundColor = '#ffffff';

  // Do not apply red color indicator for DrywallAndPaint workflow
  // Paint workflow is still under development and has no established standard for "Late" status
  if (record.phaseStructureType === 'DrywallAndPaint') {
    return { backgroundColor };
  }

  if (record.isLateHangRequest && record.phaseName === PhaseTypeEnum.Hanging) {
    backgroundColor = '#ffc0c0';
  } else if (!record.requestIsOnTime) {
    backgroundColor = '#ff8282';
  }

  return {
    backgroundColor,
  };
};

const getDefaultContractor = record => {
  const {
    suggestedContractorId,
    phaseName,
    previousBumpContractorBumperAvailable,
    previousBumpContractorBumperPaymentType,
    previousBumpContractorId,
    suggestedContractorName,
    previousBumpContractorName,
  } = record;

  if (suggestedContractorId)
    return { id: suggestedContractorId, name: suggestedContractorName };

  if (['Bump 2', 'Bump 3', 'Bump 4', 'Bump 5'].includes(phaseName)) {
    if (
      previousBumpContractorBumperAvailable &&
      previousBumpContractorBumperPaymentType === 'PerBoard'
    )
      return { id: previousBumpContractorId, name: previousBumpContractorName };
  }

  return { id: '', name: '' };
};

const selectContractor = record => {
  const defaultContractor = getDefaultContractor(record);
  return defaultContractor.id;
};

type ContractorProps = {
  confirmContractor: (
    contractorSelectedId: string,
    contractorNameSelected: string,
    jobSelectedId: string,
    jobIdSelected: string,
    isHighCeilingData: boolean,
  ) => void;
  updated: string[];
  changedJobPhase: ChangeJobPhase[];
} & PublicFieldProps &
  InjectedFieldProps<JobPendingResourceDto>;

export const Contractor: FC<ContractorProps> = props => {
  const dataProvider = useDataProvider();
  const classes = useStyles();
  const [contractors, setContractors] = useState<ContractorDto[]>([]);
  const [contractorSelected, setContractorSelected] = useState(
    selectContractor(props.record),
  );
  const [openAssignedSprayDateWarning, setOpenAssignedSprayDateWarning] =
    useState(false);
  const { hasPermission } = PermissionsProvider.useContainer();
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [alreadyGetContractors, setAlreadyGetContractors] = useState(false);

  const pageCapacity = React.useContext(PageContractorCapacityContext);

  const resourceType = props.record
    ? phaseTypeToResourceType(props.record.phaseType)
    : null;
  const { statusMap, loading: capacityLoading } = useContractorCapacityStatus(
    contractors,
    resourceType,
    alreadyGetContractors,
  );

  const getContractors = async () => {
    if (alreadyGetContractors) return;
    if (!(props.record as any)?.phaseType || !props.record?.regionId) return;
    setLoadingContractors(true);
    const contractorData: GetListResult<ContractorDto> =
      await dataProvider.getList('contractor', {
        filter: {
          ...contractorFilterForPhase(
            (props.record as any).phaseType,
            props.record.regionId,
            { highCeiling: Boolean(props.record.isHighCeiling) },
          ),
          $join: [{ field: 'contractorRegions' }],
        },
        pagination: { page: 1, perPage: PHASE_CONTRACTOR_LIST_LIMIT },
        sort: { field: 'name', order: 'ASC' },
      });
    if (contractorData.data) {
      setContractors(contractorData.data);
    } else {
      throw new Error('Job phases not found');
    }
    setAlreadyGetContractors(true);
    setLoadingContractors(false);
  };

  const onChangeContractor = event => {
    setContractorSelected(event.target.value);
  };

  const onClickSubmit = () => {
    if (
      props.record?.phaseName === 'Spraying' &&
      !props.record?.jobPhaseCustomDateStart
    ) {
      setOpenAssignedSprayDateWarning(true);
      return;
    }
    const contractor = contractors.find(
      contractor => contractor.id === contractorSelected,
    );
    const isHighCeilingCheck =
      (props.record?.isHighCeiling &&
        (props.record?.phaseName === 'Hanging' ||
          props.record?.phaseName === 'Finishing')) ||
      false;
    if (!contractor) {
      props.confirmContractor(
        contractorSelected || '',
        getDefaultContractor(props.record).name,
        props.record?.jobPhaseId || '',
        props.record?.jobId || '',
        isHighCeilingCheck,
      );
    }
    if (contractor && contractorSelected) {
      props.confirmContractor(
        contractorSelected || '',
        contractor.name,
        props.record?.jobPhaseId || '',
        props.record?.jobId || '',
        isHighCeilingCheck,
      );
    }
  };

  const getUpdated = () =>
    props.updated.indexOf(props.record?.jobPhaseId || '') !== -1;

  const checkDisabled = () => {
    if (getUpdated()) {
      return true;
    }
    if (contractorSelected) {
      return false;
    }
    return true;
  };

  const contractorsList = alreadyGetContractors
    ? contractors
    : [getDefaultContractor(props.record)];

  return (
    <Box display="flex">
      {props.record?.contractorId ? (
        <Box>
          <ContractorCapacityLabel
            name={props.record.contractorName ?? ''}
            isOverCapacity={
              pageCapacity[`${props.record.contractorId}:${resourceType}`]
                ?.isOverCapacity
            }
            variant="selected"
          />
        </Box>
      ) : (
        <Fragment>
          <Select
            onChange={onChangeContractor}
            labelId={`custom-reference-contractor`}
            id={`reference-contractor`}
            fullWidth
            onOpen={getContractors}
            value={contractorSelected}
            disabled={!hasPermission(CAN_ASSIGN_CONTRACTOR) || getUpdated()}
            renderValue={value => {
              const c = contractors.find(x => x.id === value);
              const name =
                c?.name ??
                getDefaultContractor(props.record)?.name ??
                String(value);
              const isOver = c
                ? statusMap[c.id]?.isOverCapacity
                : pageCapacity[`${value}:${resourceType}`]?.isOverCapacity;
              return (
                <ContractorCapacityLabel
                  name={name}
                  isOverCapacity={isOver}
                  variant="selected"
                />
              );
            }}
          >
            {loadingContractors || capacityLoading ? (
              <MenuItem
                disabled
                key="loading"
                value="loading"
                data-name="loading"
              >
                <CircularProgress size={16} />
              </MenuItem>
            ) : (
              contractorsList.map((item: { name: string; id: string }) => (
                <MenuItem
                  key={item.id}
                  value={item.id}
                  data-item={props.record?.jobPhaseId}
                  data-name={item.name}
                >
                  <ContractorCapacityLabel
                    name={item.name}
                    isOverCapacity={statusMap[item.id]?.isOverCapacity}
                  />
                </MenuItem>
              ))
            )}
          </Select>
          {hasPermission(CAN_ASSIGN_CONTRACTOR) && (
            <Button
              disabled={checkDisabled()}
              variant="contained"
              color="primary"
              onClick={onClickSubmit}
              className={classes.button}
            >
              Assign
            </Button>
          )}
          <InfoDialog
            handleClose={() => {
              setOpenAssignedSprayDateWarning(false);
              setContractorSelected('');
            }}
            open={openAssignedSprayDateWarning}
            content="Before a contractor can be assigned, a Spray Date must be assigned on the Production List by the relevant supervisor."
          />
        </Fragment>
      )}
    </Box>
  );
};

const workdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const Workday = props => {
  const getWorkday = () => {
    const dateOfWorkday = new Date(props.record.jobPhaseDateRequestDue);
    dateOfWorkday.setDate(dateOfWorkday.getDate() + 1);
    return workdays[dateOfWorkday.getDay()];
  };

  const handleChange = event => {
    props.setChangedJobPhase([
      ...props.changedJobPhase,
      {
        jobPhaseId: props.record.jobPhaseId,
        requestDelayDays: event.target.value,
      },
    ]);
  };

  const getUpdated = () =>
    props.updated.indexOf(props.record.jobPhaseId) !== -1;

  return (
    <Box>
      {getWorkday() === workdays[1] || getWorkday() === workdays[6] ? (
        <Select
          style={{ width: '70px' }}
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          label="Age"
          onChange={handleChange}
          defaultValue={getWorkday() === 'Sat' ? 0 : 2}
          disabled={getUpdated()}
        >
          <MenuItem value={0}>{workdays[6]}</MenuItem>
          <MenuItem value={2}>{workdays[1]}</MenuItem>
        </Select>
      ) : (
        <Typography>{getWorkday()}</Typography>
      )}
    </Box>
  );
};

const ListActions = props => {
  const { className, filters, maxResults, ...rest } = props;
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    showFilter,
    total,
  } = useListContext();

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      <CustomExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
        maxResults={maxResults}
      />
    </TopToolbar>
  );
};

const ContractorCapacityProvider: FC = ({ children }) => {
  const { data, ids } = useListContext();
  const records: PreSelectedRecord[] = React.useMemo(() => {
    if (!data || !ids) return [];
    return ids
      .map(id => {
        const record = data[id];
        if (!record) return null;
        const contractorId =
          record.contractorId || getDefaultContractor(record).id;
        if (!contractorId) return null;
        const rt = phaseTypeToResourceType(record.phaseType);
        if (!rt) return null;
        return { contractorId, resourceType: rt };
      })
      .filter(Boolean) as PreSelectedRecord[];
  }, [data, ids]);

  const { statusMap } = usePreSelectedContractorCapacity(
    records,
    records.length > 0,
  );

  return (
    <PageContractorCapacityContext.Provider value={statusMap}>
      {children}
    </PageContractorCapacityContext.Provider>
  );
};

type ChangeJobPhase = Pick<JobPhaseOwnableInterface, 'jobPhaseId'> &
  JobPhaseInterface;

export const MasterSheetList: FC<ListProps> = props => {
  const classes = useStyles();
  const notify = useNotify();
  const { assign } = useAssignContractor();
  const { opened } = UIProvider.useContainer();

  const [openConfirm, setOpenConfirm] = useState(false);
  const [openConfirmWarning, setOpenConfirmWarning] = useState(false);
  const [openHighCeilingConfirm, setOpenHighCeilingConfirm] = useState(false);
  const [contractorSelected, setContractorSelected] = useState('');
  const [contractorName, setContractorName] = useState('');
  const [jobSelected, setJobSelected] = useState('');
  const [updated, setUpdated] = useState<string[]>([]);
  const [changedJobPhase, setChangedJobPhase] = useState<ChangeJobPhase[]>([]);

  const onClose = () => {
    setOpenConfirm(false);
    setOpenConfirmWarning(false);
    setOpenHighCeilingConfirm(false);
  };

  const confirmContractor = (
    contractorSelectedId: string,
    contractorNameSelected: string,
    jobSelectedId: string,
    // the job id is no longer needed: the API checks previous assignments
    _jobIdSelected: string,
    _isHighCeilingData: boolean,
  ) => {
    setContractorName(contractorNameSelected);
    setContractorSelected(contractorSelectedId);
    setJobSelected(jobSelectedId);
    // removed from now, we will back on thia la
    // if (isHighCeilingData) {
    //   setOpenHighCeilingConfirm(true);
    // } else {
    setOpenConfirm(true);
    // }
  };

  /**
   * One POST job-phase/:id/assign-contractor carries the contractor and the
   * Workday delay edited in the row. Without the acknowledgement the API
   * answers 409 when this contractor already worked another phase of the
   * house, which opens the warning dialog; confirming it re-sends with the
   * acknowledgement.
   */
  const assignContractor = async (acknowledgePreviousAssignment = false) => {
    try {
      const changedPhase = changedJobPhase.find(
        changed => changed.jobPhaseId === jobSelected,
      );
      const result = await assign(jobSelected, {
        contractorId: contractorSelected,
        ...(changedPhase && {
          requestDelayDays: changedPhase.requestDelayDays,
        }),
        ...(acknowledgePreviousAssignment && {
          acknowledgePreviousAssignment: true,
        }),
      });

      if (result.status === 'previously-assigned') {
        setOpenConfirm(false);
        setOpenConfirmWarning(true);
        return;
      }

      setUpdated([...updated, result.jobPhase.id]);
      notify('Contractor Assigned!');
    } catch (error) {
      notify(error.message, 'warning');
    }
    onClose();
  };

  const onConfirmContractor = () => assignContractor();
  const onConfirmPreviousAssignment = () => assignContractor(true);

  useEffect(() => {
    trackEvent('PageView', {
      resource: 'rm-master-sheet',
      page: window.location.pathname,
      role: localStorage.getItem('userRoles'),
      env: process.env.REACT_APP_MIXPANEL_ENV,
    });
  }, []);

  return (
    <Fragment>
      <List
        {...props}
        exporter={exporter('rm-master-sheet')}
        filters={<RmMasterSheetFilter>{props.filter}</RmMasterSheetFilter>}
        filterDefaultValues={{ 'contractorHasBeenAssigned||$eq': false }}
        sort={{ field: 'requestIsOnTime', order: 'ASC' }}
        actions={<ListActions maxResults={20000} />}
        bulkActionButtons={false}
        classes={{
          content: opened ? classes.content : classes.contentClosed,
          root: opened ? classes.root : classes.rootClosed,
        }}
        pagination={<Pagination rowsPerPageOptions={[5, 10, 25, 50]} />}
        perPage={5}
      >
        <ContractorCapacityProvider>
          <Datagrid rowStyle={lateVerify}>
            <SupervisorField
              label="Supervisors"
              sortable={true}
              sortBy="supervisorFullNames"
            />
            <TextField source="subdivisionName" label="Subdivision" />
            <TextField source="jobLot" label="Lot" />
            <TextField source="jobBlock" label="Block" />
            <TextField source="builderName" label="Builder" />
            <FunctionField
              source="phaseStructureType"
              label="Workflow"
              render={(record: any) =>
                record?.phaseStructureType === 'DrywallAndPaint'
                  ? 'Drywall and Paint'
                  : 'Drywall'
              }
            />
            <TextField source="phaseName" label="Phase" />
            <WithoutTimezoneDateField
              source="jobPhaseDateRequested"
              label="Phase 1st Request Date"
            />
            <TextField source="phaseDaysCount" label="Phase Business Days" />
            <TextField source="jobDaysCount" label="Job Business Days" />
            <Workday
              source="jobPhaseDateRequestDue"
              label="Workday"
              updated={updated}
              setChangedJobPhase={setChangedJobPhase}
              changedJobPhase={changedJobPhase}
            />
            <Ready source="jobPhaseReadyHour" label="Ready?" />
            <TextField source="payableBoardsTotal" label="Payable Boards" />
            <TextField
              source="payableBoardsReceiptTotal"
              label="Supervisor Count"
            />
            <TextField source="totalSquareFootage" label="Total Sq Ft" />
            <Contractor
              label="Contractor"
              updated={updated}
              changedJobPhase={changedJobPhase}
              confirmContractor={confirmContractor}
            />
          </Datagrid>
        </ContractorCapacityProvider>
      </List>
      <ConfirmDialog
        open={openConfirm}
        title={`Are you sure that you want to assign the contractor ${contractorName}?`}
        handleClose={onClose}
        onConfirm={onConfirmContractor}
      />
      <ConfirmDialog
        showWarning
        open={openConfirmWarning}
        title={`You've previously assigned this contractor to this house. Are you sure you want to continue?`}
        handleClose={onClose}
        onConfirm={onConfirmPreviousAssignment}
      />
      <ConfirmDialog
        open={openHighCeilingConfirm}
        content={
          <Box textAlign="center">
            <DialogTitle style={{ color: 'red' }} id="alert-dialog-title">
              Caution: This is a High-Ceiling Model! Check that the assigned
              contractor has the expertise to handle elevated structures.
            </DialogTitle>
            <DialogTitle
              style={{ color: 'black' }}
              id="alert-dialog-title"
            >{`Are you sure that you want to assign the contractor ${contractorName}?`}</DialogTitle>
          </Box>
        }
        title={''}
        handleClose={onClose}
        onConfirm={onConfirmContractor}
      />
    </Fragment>
  );
};
